import { useEffect, useMemo, useState } from "react";
import { Button } from "@swot/base-components";
import { CmtsSmartDataGridToolbar } from "./CmtsSmartDataGridToolbar.js";
import { CmtsSmartDataGridPagination } from "./CmtsSmartDataGridPagination.js";
import { CmtsSmartDataGridColumnManager } from "./CmtsSmartDataGridColumnManager.js";
import { CmtsSmartDataGridEmptyState } from "./CmtsSmartDataGridEmptyState.js";
import { CmtsSmartDataGridErrorState } from "./CmtsSmartDataGridErrorState.js";
import { CmtsSmartDataGridResponsiveCard } from "./CmtsSmartDataGridResponsiveCard.js";
import type { CmtsGridColumn, CmtsGridFilterControl, CmtsGridFilterOption, CmtsGridRowAction, CmtsSmartDataGridProps } from "./CmtsSmartDataGridTypes.js";
import { CmtsApiError, throwCmtsCanonicalError } from "@cmts/api/cmtsApiErrors.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";
import { CmtsEnterpriseModal } from "@components/CmtsEnterpriseModal.js";

type GridExportFormat = "csv" | "xls" | "json";

function safeFileName(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "cmts-export";
}

function downloadBlob(content: BlobPart, mime: string, fileName: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function getCellValue<TRecord extends Record<string, unknown>>(record: TRecord, column: CmtsGridColumn<TRecord>): unknown {
  return column.accessor ? column.accessor(record) : record[column.id];
}
function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}
function normalize(value: unknown): string {
  return displayValue(value).trim().toLowerCase();
}
function filterValueMatches(cellValue: unknown, selectedValue: string, operator: "contains" | "equals" | "startsWith" = "contains"): boolean {
  if (!selectedValue || selectedValue === "all") return true;
  const current = normalize(cellValue);
  const expected = selectedValue.trim().toLowerCase();
  if (operator === "equals") return current === expected;
  if (operator === "startsWith") return current.startsWith(expected);
  return current.includes(expected) || expected.includes(current);
}
function isActionHidden<TRecord extends Record<string, unknown>>(action: CmtsGridRowAction<TRecord>, record: TRecord): boolean {
  return typeof action.hidden === "function" ? action.hidden(record) : Boolean(action.hidden);
}
function isActionDisabled<TRecord extends Record<string, unknown>>(action: CmtsGridRowAction<TRecord>, record: TRecord): boolean {
  return typeof action.disabled === "function" ? action.disabled(record) : Boolean(action.disabled);
}
function actionDisabledReason<TRecord extends Record<string, unknown>>(action: CmtsGridRowAction<TRecord>, record: TRecord): string | undefined {
  const reason = typeof action.disabledReason === "function" ? action.disabledReason(record) : action.disabledReason;
  return typeof reason === "string" && reason.trim() ? reason.trim() : undefined;
}
function createColumnFilter<TRecord extends Record<string, unknown>>(column: CmtsGridColumn<TRecord>): CmtsGridFilterControl<TRecord> {
  return { id: column.id, label: column.header, columnId: column.id, operator: "contains", placeholder: `All ${column.header}` };
}
function buildFilterOptions<TRecord extends Record<string, unknown>>(rows: readonly TRecord[], columns: readonly CmtsGridColumn<TRecord>[], filter: CmtsGridFilterControl<TRecord>): readonly CmtsGridFilterOption[] {
  if (filter.options?.length) return filter.options;
  const column = columns.find((candidate) => candidate.id === filter.columnId);
  if (!column) return [];
  const counts = new Map<string, { label: string; count: number }>();
  rows.forEach((record) => {
    const raw = getCellValue(record, column);
    const label = displayValue(raw);
    if (label === "-") return;
    const key = label.trim().toLowerCase();
    const existing = counts.get(key);
    counts.set(key, { label, count: (existing?.count ?? 0) + 1 });
  });
  return Array.from(counts.entries())
    .sort((a, b) => a[1].label.localeCompare(b[1].label, undefined, { numeric: true, sensitivity: "base" }))
    .map(([value, meta]) => ({ value, label: meta.label, count: meta.count }));
}

const EMPTY_ROWS: readonly never[] = [];

export function CmtsSmartDataGrid<TRecord extends Record<string, unknown>>(props: CmtsSmartDataGridProps<TRecord>) {
  const rows = Array.isArray(props.rows) ? props.rows : EMPTY_ROWS;
  const usableColumns = useMemo(() => props.columns.filter((column) => column.visible !== false), [props.columns]);
  const [searchText, setSearchText] = useState(props.initialSearchText ?? "");
  const [sortColumn, setSortColumn] = useState<string | null>(() => usableColumns.find((column) => column.sortable)?.id ?? usableColumns[0]?.id ?? null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [pageSize, setPageSize] = useState(props.defaultPageSize ?? 10);
  const [pageIndex, setPageIndex] = useState(0);
  const [visibleColumnIds, setVisibleColumnIds] = useState(() => usableColumns.map((column) => column.id));
  const [filterValues, setFilterValues] = useState<Record<string, string>>(() => Object.fromEntries(Object.entries(props.initialFilterValues ?? {}).map(([key, value]) => [key, value ?? "all"])));
  const [actionState, setActionState] = useState<{ pendingKey?: string | undefined; error?: { actionLabel: string; rowId?: string | undefined; message: string; diagnostics?: unknown | undefined } | undefined }>({});
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<GridExportFormat>("csv");
  const visibleColumns = usableColumns.filter((column) => visibleColumnIds.includes(column.id));
  const pageSizeOptions = props.pageSizeOptions ?? [10, 25, 50, 100];
  const searchable = props.searchable ?? true;
  const sortable = props.sortable ?? true;
  const gridFilters = useMemo(() => {
    const configured = props.filters?.length ? props.filters : (props.filterable === false ? [] : usableColumns.filter((column) => column.filterable).map(createColumnFilter));
    return configured.map((filter) => ({ ...filter, resolvedOptions: buildFilterOptions(rows, usableColumns, filter) }));
  }, [props.filters, props.filterable, usableColumns, rows]);

  useEffect(() => {
    setFilterValues((current) => {
      const next = { ...current };
      for (const [key, value] of Object.entries(props.initialFilterValues ?? {})) next[key] = value ?? "all";
      return next;
    });
  }, [props.initialFilterValues]);

  useEffect(() => {
    setSearchText(props.initialSearchText ?? "");
    setPageIndex(0);
  }, [props.initialSearchText]);

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const searched = q ? rows.filter((record) => usableColumns.some((column) => displayValue(getCellValue(record, column)).toLowerCase().includes(q))) : rows;
    const filtered = searched.filter((record) => gridFilters.every((filter) => {
      const selected = filterValues[filter.id] ?? "all";
      if (!selected || selected === "all") return true;
      if (filter.predicate) return filter.predicate(record, selected);
      const column = usableColumns.find((candidate) => candidate.id === filter.columnId);
      if (!column) return true;
      return filterValueMatches(getCellValue(record, column), selected, filter.operator ?? "contains");
    }));
    const sorted = sortColumn ? [...filtered].sort((a, b) => {
      const column = usableColumns.find((c) => c.id === sortColumn);
      if (!column) return 0;
      const av = displayValue(getCellValue(a, column)).toLowerCase();
      const bv = displayValue(getCellValue(b, column)).toLowerCase();
      const result = av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" });
      return sortDirection === "asc" ? result : -result;
    }) : filtered;
    return sorted;
  }, [rows, searchText, usableColumns, sortColumn, sortDirection, gridFilters, filterValues]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePageIndex = Math.min(pageIndex, totalPages - 1);
  const pageRows = filteredRows.slice(safePageIndex * pageSize, safePageIndex * pageSize + pageSize);
  const handleExport = async (): Promise<void> => {
    setExportOpen(true);
  };
  const performExport = async (): Promise<void> => {
    try {
      setActionState({ pendingKey: "grid-export" });
      if (props.onExport) {
        await props.onExport(filteredRows);
      } else {
        const exportColumns = visibleColumns.filter((column) => column.id.toLowerCase() !== "actions");
        const records = filteredRows.map((row) => Object.fromEntries(exportColumns.map((column) => [column.header, displayValue(getCellValue(row, column))])));
        const base = `${safeFileName(props.title)}-${new Date().toISOString().slice(0, 10)}`;
        if (exportFormat === "json") {
          downloadBlob(JSON.stringify(records, null, 2), "application/json;charset=utf-8", `${base}.json`);
        } else {
          const escape = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
          const csv = [exportColumns.map((column) => escape(column.header)).join(","), ...records.map((record) => exportColumns.map((column) => escape(record[column.header])).join(","))].join("\r\n");
          if (exportFormat === "csv") downloadBlob(`\uFEFF${csv}`, "text/csv;charset=utf-8", `${base}.csv`);
          else {
            const htmlEscape = (value: unknown) => String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
            const table = `<table><thead><tr>${exportColumns.map((column) => `<th>${htmlEscape(column.header)}</th>`).join("")}</tr></thead><tbody>${records.map((record) => `<tr>${exportColumns.map((column) => `<td>${htmlEscape(record[column.header])}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
            downloadBlob(`\uFEFF<html><head><meta charset="utf-8"></head><body>${table}</body></html>`, "application/vnd.ms-excel;charset=utf-8", `${base}.xls`);
          }
        }
      }
      setExportOpen(false);
      setActionState({});
    } catch (error) {
      setActionState({ error: { actionLabel: "Export", message: actionErrorMessage(error), diagnostics: props.developerMode ? error : undefined } });
    }
  };
  const handleRefresh = async (): Promise<void> => {
    try {
      setActionState({ pendingKey: "grid-refresh" });
      if (!props.onRefresh) {
        throwCmtsCanonicalError({ code: "CMTS_ERR_SIDE_EFFECT_UNAVAILABLE", message: "SmartGrid refresh is enabled but no repository/offline refresh handler is connected.", details: { gridTitle: props.title, action: "refresh" } });
      }
      await props.onRefresh();
      setActionState({});
    } catch (error) {
      setActionState({ error: { actionLabel: "Refresh", message: actionErrorMessage(error), diagnostics: props.developerMode ? error : undefined } });
    }
  };
  const [actionsDrawerRow, setActionsDrawerRow] = useState<TRecord | null>(null);
  const actions = props.actions ?? [];
  const recordId = (record: TRecord): string => String((props.rowKey ? record[props.rowKey] : record.id) ?? "row");
  const actionKey = (action: CmtsGridRowAction<TRecord>, record: TRecord): string => `${action.id}:${recordId(record)}`;
  const actionErrorMessage = (error: unknown): string => error instanceof CmtsApiError ? normalizeUserSafeApiError(error).userMessage : normalizeUserSafeApiError(error).userMessage || "The row action failed. Review diagnostics and retry.";
  const runRowAction = async (action: CmtsGridRowAction<TRecord>, record: TRecord): Promise<void> => {
    try {
      const result = action.onClick(record);
      if (result && typeof (result as Promise<unknown>).then === "function") {
        const key = actionKey(action, record);
        setActionState({ pendingKey: key });
        try {
          await result;
        } finally {
          setActionState({});
        }
      }
    } catch (error) {
      setActionState({ error: { actionLabel: action.label, rowId: recordId(record), message: actionErrorMessage(error), diagnostics: props.developerMode ? error : undefined } });
    }
  };

  const changeSort = (column: CmtsGridColumn<TRecord>) => {
    if (!sortable || column.sortable === false) return;
    if (sortColumn === column.id) setSortDirection((current) => current === "asc" ? "desc" : "asc");
    else { setSortColumn(column.id); setSortDirection("asc"); }
  };
  const setGridFilter = (filterId: string, value: string) => {
    setFilterValues((current) => ({ ...current, [filterId]: value }));
    setPageIndex(0);
  };
  const clearFilters = () => {
    setSearchText("");
    setFilterValues({});
    setPageIndex(0);
  };

  if (props.error) return <CmtsSmartDataGridErrorState error={props.error} />;
  if (usableColumns.length === 0) return props.developerMode ? <CmtsSmartDataGridErrorState error="GRID.NO_COLUMNS: At least one column definition is required." /> : <CmtsSmartDataGridErrorState error="The table configuration is not available. Please refresh or contact the administrator." />;

  return (
    <section className="cmts-smart-grid" data-default-page-size={props.defaultPageSize ?? 10} data-grid-title={props.title} data-filterable={gridFilters.length > 0 ? "true" : "false"}>
      <CmtsSmartDataGridToolbar<TRecord>
        title={props.title}
        subtitle={props.subtitle}
        searchable={searchable}
        searchText={searchText}
        searchPlaceholder={props.searchPlaceholder}
        onSearchChange={(value) => { setSearchText(value); setPageIndex(0); }}
        filters={gridFilters}
        filterValues={filterValues}
        onFilterChange={setGridFilter}
        onClearFilters={clearFilters}
        exportable={Boolean(props.exportable)}
        refreshable={Boolean(props.refreshable)}
        onExport={props.exportable ? handleExport : undefined}
        onRefresh={props.refreshable ? handleRefresh : undefined}
        filterSummary={props.filterSummary ?? []}
      />
      <CmtsSmartDataGridColumnManager columns={usableColumns} visibleColumnIds={visibleColumnIds} onToggle={(columnId) => setVisibleColumnIds((current) => current.includes(columnId) ? current.filter((id) => id !== columnId) : [...current, columnId])} />
      {actionState.error ? <div className="cmts-smart-grid-action-error" role="alert"><strong>{actionState.error.actionLabel} failed</strong><span>{actionState.error.message}</span></div> : null}
      <CmtsEnterpriseModal
  open={exportOpen}
  title="Export records"
  subtitle="Download the currently displayed task records."
  size="md"
  onClose={() => {
    if (actionState.pendingKey !== "grid-export") {
      setExportOpen(false);
    }
  }}
>
  <div className="cmts-export-dialog">
    <section className="cmts-export-dialog__hero">
      <div className="cmts-export-dialog__hero-icon">
        <span aria-hidden="true">⇩</span>
      </div>

      <div className="cmts-export-dialog__hero-content">
        <span className="cmts-export-dialog__eyebrow">
          DATA EXPORT
        </span>

        <h3>Prepare your export</h3>

        <p>
          Select a file format and download the filtered records
          using the currently visible columns.
        </p>
      </div>

      <span className="cmts-export-dialog__count">
        {filteredRows.length}{" "}
        {filteredRows.length === 1 ? "record" : "records"}
      </span>
    </section>

    <section className="cmts-export-dialog__section">
      <div className="cmts-export-dialog__section-heading">
        <div>
          <span className="cmts-export-dialog__section-number">
            01
          </span>

          <div>
            <strong>Select file format</strong>
            <small>
              Choose the format required for this download.
            </small>
          </div>
        </div>

        <span className="cmts-export-dialog__required">
          Required
        </span>
      </div>

      <div
        className="cmts-export-dialog__formats"
        role="radiogroup"
        aria-label="Export file format"
      >
        <button
          type="button"
          role="radio"
          aria-checked={exportFormat === "csv"}
          className={[
            "cmts-export-format-card",
            exportFormat === "csv"
              ? "cmts-export-format-card--selected"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setExportFormat("csv")}
        >
          <span className="cmts-export-format-card__icon cmts-export-format-card__icon--csv">
            CSV
          </span>

          <span className="cmts-export-format-card__content">
            <strong>CSV spreadsheet</strong>
            <small>
              Best for Excel, data imports and table analysis.
            </small>
          </span>

          <span className="cmts-export-format-card__check">
            {exportFormat === "csv" ? "✓" : ""}
          </span>
        </button>

        <button
          type="button"
          role="radio"
          aria-checked={exportFormat === "xls"}
          className={[
            "cmts-export-format-card",
            exportFormat === "xls"
              ? "cmts-export-format-card--selected"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setExportFormat("xls")}
        >
          <span className="cmts-export-format-card__icon cmts-export-format-card__icon--excel">
            XLS
          </span>

          <span className="cmts-export-format-card__content">
            <strong>Excel workbook</strong>
            <small>
              Formatted spreadsheet for business reporting.
            </small>
          </span>

          <span className="cmts-export-format-card__check">
            {exportFormat === "xls" ? "✓" : ""}
          </span>
        </button>

        <button
          type="button"
          role="radio"
          aria-checked={exportFormat === "json"}
          className={[
            "cmts-export-format-card",
            exportFormat === "json"
              ? "cmts-export-format-card--selected"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setExportFormat("json")}
        >
          <span className="cmts-export-format-card__icon cmts-export-format-card__icon--json">
            JSON
          </span>

          <span className="cmts-export-format-card__content">
            <strong>JSON data</strong>
            <small>
              Structured data for development and integrations.
            </small>
          </span>

          <span className="cmts-export-format-card__check">
            {exportFormat === "json" ? "✓" : ""}
          </span>
        </button>
      </div>
    </section>

    <aside className="cmts-export-dialog__notice">
      <span
        className="cmts-export-dialog__notice-icon"
        aria-hidden="true"
      >
        i
      </span>

      <div>
        <strong>Current view will be exported</strong>
        <p>
          Active search, filters, sorting and visible columns
          will be applied to the downloaded file.
        </p>
      </div>
    </aside>

    {filteredRows.length === 0 ? (
      <div
        className="cmts-export-dialog__empty"
        role="alert"
      >
        No matching records are available to export. Clear or
        change the current filters and try again.
      </div>
    ) : null}

    <footer className="cmts-export-dialog__footer">
      <Button
        label="Cancel"
        variant="ghost"
        onClick={() => setExportOpen(false)}
        disabled={actionState.pendingKey === "grid-export"}
      />

      <Button
        label={
          actionState.pendingKey === "grid-export"
            ? "Preparing export..."
            : `Download ${exportFormat.toUpperCase()}`
        }
        onClick={() => void performExport()}
        disabled={
          actionState.pendingKey === "grid-export" ||
          filteredRows.length === 0
        }
      />
    </footer>
  </div>
</CmtsEnterpriseModal>
      {props.loading ? <div className="cmts-smart-grid-state cmts-smart-grid-state--loading">Loading records...</div> : null}
      {!props.loading && filteredRows.length === 0 ? <CmtsSmartDataGridEmptyState message={props.emptyMessage} /> : null}
      {filteredRows.length > 0 ? <>
        <div className="cmts-smart-grid-table-scroll">
          <table className="cmts-smart-grid-table">
            <thead>
              <tr>
                {visibleColumns.map((column) => (
                  <th key={column.id} style={{ minWidth: column.minWidth, width: column.width, textAlign: column.align ?? "left" }}>
                    <Button
                      label={`${column.header}${sortColumn === column.id ? ` ${sortDirection === "asc" ? "↑" : "↓"}` : ""}`}
                      variant="ghost"
                      onClick={() => changeSort(column)}
                      disabled={column.sortable === false}
                    />
                  </th>
                ))}
                {actions.length > 0 ? <th style={{ textAlign: "center", width: 140, minWidth: 140, whiteSpace: "nowrap" }}>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((record, index) => {
                const keyValue = props.rowKey ? record[props.rowKey] : record.id;
                return (
                  <tr
                    key={String(keyValue ?? index)}
                    className={props.onRowClick ? "cmts-smart-grid-row--clickable" : undefined}
                    onClick={props.onRowClick ? async () => { await props.onRowClick?.(record); } : undefined}
                  >
                    {visibleColumns.map((column) => (
                      <td key={column.id} style={{ textAlign: column.align ?? "left" }}>
                        {column.render ? column.render(record) : displayValue(getCellValue(record, column))}
                      </td>
                    ))}
                    {actions.length > 0 ? (
                      <td className="cmts-smart-grid-actions" style={{ textAlign: "center", width: 140, minWidth: 140, whiteSpace: "nowrap" }}>
                        <button
                          type="button"
                          className="cmts-notification-actions-button"
                          aria-label={`Open actions for ${recordId(record)}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setActionsDrawerRow(record);
                          }}
                        >
                          <span className="cmts-notification-actions-button__icon" aria-hidden="true">
                            ⋮
                          </span>
                          <span>Actions</span>
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Floating Actions Drawer Modal for Data Grid */}
        <CmtsEnterpriseModal
          open={Boolean(actionsDrawerRow)}
          title="Record actions"
          subtitle={
            actionsDrawerRow
              ? `${String((actionsDrawerRow as Record<string, unknown>).code ?? (actionsDrawerRow as Record<string, unknown>).id ?? "")} • ${String((actionsDrawerRow as Record<string, unknown>).label ?? (actionsDrawerRow as Record<string, unknown>).name ?? (actionsDrawerRow as Record<string, unknown>).title ?? "")}`
              : ""
          }
          size="md"
          onClose={() => setActionsDrawerRow(null)}
        >
          {actionsDrawerRow && (
            <div className="cmts-notification-action-modal">
              <div className="cmts-notification-action-modal__summary">
                <div>
                  <span>Current status</span>
                  <strong
                    style={{
                      color:
                        (actionsDrawerRow as Record<string, unknown>).active !== false &&
                        (actionsDrawerRow as Record<string, unknown>).status !== "Inactive"
                          ? "#059669"
                          : "#64748b",
                    }}
                  >
                    {String(
                      (actionsDrawerRow as Record<string, unknown>).status ??
                        ((actionsDrawerRow as Record<string, unknown>).active !== false ? "ACTIVE" : "INACTIVE")
                    ).toUpperCase()}
                  </strong>
                </div>
                <div>
                  <span>Record Code</span>
                  <code>{String((actionsDrawerRow as Record<string, unknown>).code ?? (actionsDrawerRow as Record<string, unknown>).id ?? "-")}</code>
                </div>
                <div>
                  <span>Updated</span>
                  <strong>{String((actionsDrawerRow as Record<string, unknown>).updatedAt ?? (actionsDrawerRow as Record<string, unknown>).updated ?? "-")}</strong>
                </div>
              </div>

              {/* Normal Action Cards Grid (2 Equal Columns) */}
              <div className="cmts-notification-action-modal__grid">
                {actions
                  .filter((action) => {
                    if (isActionHidden(action, actionsDrawerRow)) return false;
                    const isDanger =
                      action.id === "delete" ||
                      action.id === "deactivate" ||
                      action.label.toLowerCase().includes("delete") ||
                      action.label.toLowerCase().includes("deactivate") ||
                      action.label.toLowerCase().includes("archive");
                    return !isDanger;
                  })
                  .map((action) => {
                    const disabledReason = actionDisabledReason(action, actionsDrawerRow);
                    const disabled = isActionDisabled(action, actionsDrawerRow);
                    const pending = actionState.pendingKey === actionKey(action, actionsDrawerRow);

                    const actionIcon =
                      action.id === "edit"
                        ? "✎"
                        : action.id === "detail"
                        ? "🔍"
                        : action.id === "activate"
                        ? "✓"
                        : action.id === "test"
                        ? "⚡"
                        : action.id === "preview"
                        ? "👁"
                        : "›";

                    return (
                      <button
                        key={action.id}
                        type="button"
                        className="cmts-notification-action-option"
                        disabled={disabled || pending}
                        title={disabledReason ?? action.title}
                        onClick={async () => {
                          const target = actionsDrawerRow;
                          setActionsDrawerRow(null);
                          await runRowAction(action, target);
                        }}
                      >
                        <span className="cmts-notification-action-option__icon" aria-hidden="true">
                          {actionIcon}
                        </span>
                        <span className="cmts-notification-action-option__content">
                          <strong>{pending ? "Working..." : action.label}</strong>
                          <small>{disabledReason ?? action.title ?? `Execute ${action.label} on this record.`}</small>
                        </span>
                        <span className="cmts-notification-action-option__arrow" aria-hidden="true">
                          ›
                        </span>
                      </button>
                    );
                  })}
              </div>

              {/* Destructive / Danger Action Cards (Full-Width Bottom) */}
              {actions.some((action) => {
                if (isActionHidden(action, actionsDrawerRow)) return false;
                return (
                  action.id === "delete" ||
                  action.id === "deactivate" ||
                  action.label.toLowerCase().includes("delete") ||
                  action.label.toLowerCase().includes("deactivate") ||
                  action.label.toLowerCase().includes("archive")
                );
              }) ? (
                <div className="cmts-notification-action-modal__danger">
                  {actions
                    .filter((action) => {
                      if (isActionHidden(action, actionsDrawerRow)) return false;
                      return (
                        action.id === "delete" ||
                        action.id === "deactivate" ||
                        action.label.toLowerCase().includes("delete") ||
                        action.label.toLowerCase().includes("deactivate") ||
                        action.label.toLowerCase().includes("archive")
                      );
                    })
                    .map((action) => {
                      const disabledReason = actionDisabledReason(action, actionsDrawerRow);
                      const disabled = isActionDisabled(action, actionsDrawerRow);
                      const pending = actionState.pendingKey === actionKey(action, actionsDrawerRow);

                      const actionIcon =
                        action.id === "delete"
                          ? "✕"
                          : action.id === "deactivate"
                          ? "⊘"
                          : "✕";

                      return (
                        <button
                          key={action.id}
                          type="button"
                          className="cmts-notification-action-option cmts-notification-action-option--danger"
                          disabled={disabled || pending}
                          title={disabledReason ?? action.title}
                          onClick={async () => {
                            const target = actionsDrawerRow;
                            setActionsDrawerRow(null);
                            await runRowAction(action, target);
                          }}
                        >
                          <span className="cmts-notification-action-option__icon" aria-hidden="true">
                            {actionIcon}
                          </span>
                          <span className="cmts-notification-action-option__content">
                            <strong>{pending ? "Working..." : action.label}</strong>
                            <small>{disabledReason ?? action.title ?? `Execute ${action.label} on this record.`}</small>
                          </span>
                          <span className="cmts-notification-action-option__arrow" aria-hidden="true">
                            ›
                          </span>
                        </button>
                      );
                    })}
                </div>
              ) : null}
            </div>
          )}
        </CmtsEnterpriseModal>

        <div className="cmts-smart-grid-mobile-list">{pageRows.map((record, index) => <CmtsSmartDataGridResponsiveCard key={String((props.rowKey ? record[props.rowKey] : record.id) ?? index)} record={record} columns={visibleColumns} actions={actions.filter((action) => !isActionHidden(action, record))} isActionDisabled={isActionDisabled} actionDisabledReason={actionDisabledReason} isActionPending={(action, row) => actionState.pendingKey === actionKey(action, row)} onActionClick={runRowAction} onRowClick={props.onRowClick} />)}</div>
        <CmtsSmartDataGridPagination pageIndex={safePageIndex} pageSize={pageSize} totalRows={filteredRows.length} pageSizeOptions={pageSizeOptions} onPrevious={() => setPageIndex((p) => Math.max(0, p - 1))} onNext={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))} onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }} />
      </> : null}
    </section>
  );
}
